import { Box, Typography, Avatar, CircularProgress, IconButton, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useEffect, useState, useCallback, useMemo } from "react";
import { getAllUsers } from "../utils/api";
import { Link } from "react-router-dom";

const USERS_PER_PAGE = 4;

const UserCard = ({ user }) => {
  const initials = `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase();

  return (
    <Link to={`/profile/${user._id}`} style={{ textDecoration: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 0.8,
          py: 0.7,
          borderRadius: "9px",
          transition: "background 0.15s",
          "&:hover": { bgcolor: "rgba(212,175,55,0.09)" },
        }}
      >
        <Avatar
          src={user.profilePicture}
          alt={`${user.firstName} ${user.lastName}`}
          sx={{
            width: 36,
            height: 36,
            border: "2px solid #F3F4F6",
            bgcolor: "#D4AF37",
            color: "#1A1A2E",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontWeight: 700,
              fontSize: 12.5,
              color: "#111827",
              lineHeight: 1.2,
            }}
          >
            {user.firstName} {user.lastName}
          </Typography>
          {user.work && (
            <Typography
              noWrap
              sx={{ fontSize: 11, color: "#9CA3AF", mt: 0.1 }}
            >
              {user.work}
            </Typography>
          )}
        </Box>
      </Box>
    </Link>
  );
};

const AddFriends = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchUsers = useCallback(async () => {
    try {
      const users = await getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const totalPages = Math.ceil(allUsers.length / USERS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * USERS_PER_PAGE;
    return allUsers.slice(start, start + USERS_PER_PAGE);
  }, [allUsers, currentPage]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} sx={{ color: "#D4AF37" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <PeopleAltOutlinedIcon sx={{ color: "#D4AF37", fontSize: 16 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1F2937" }}>
          People you may know
        </Typography>
      </Box>

      {paginatedUsers.length === 0 ? (
        <Typography sx={{ color: "#9CA3AF", fontSize: 12.5, textAlign: "center", py: 2 }}>
          No users found.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          {paginatedUsers.map((user) => (
            <UserCard key={user._id} user={user} />
          ))}
        </Box>
      )}

      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            mt: 1.5,
          }}
        >
          <Tooltip title="Previous">
            <span>
              <IconButton
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                sx={{
                  width: 26,
                  height: 26,
                  bgcolor: "#F3F4F6",
                  "&:hover": { bgcolor: "#E5E7EB" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Typography sx={{ fontSize: 11.5, color: "#6B7280", fontWeight: 600, px: 0.5 }}>
            {currentPage} / {totalPages}
          </Typography>

          <Tooltip title="Next">
            <span>
              <IconButton
                size="small"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                sx={{
                  width: 26,
                  height: 26,
                  bgcolor: "#F3F4F6",
                  "&:hover": { bgcolor: "#E5E7EB" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default AddFriends;
